// Paste from Kindle (book page Part 6; decided Oct 20 and 21, notebook page format from Kristi's
// Kill Process paste, Oct 27): reads what you paste from one of three places and turns it into
// quotes (highlights) and reading notes (your Kindle notes), matched against what's already saved.
//   1. The Kindle notebook page (read.amazon.com/notebook), selected and copied:
//        Yellow highlight | Location: 169
//        Options
//        I wish I could be unbroken.
//        Note:here is an example note          ← a note on the highlight above
//   2. The Export notebook email (copied as text): section headings, then
//        Highlight (yellow) - Page 7 · Location 229   /   Note - Location 229
//   3. My Clippings.txt from a Kindle e-reader: entries between "==========" lines,
//        Title (Author) / - Your Highlight on page 12 | Location 169-170 | Added on …
//      (only this book's entries are read; bookmarks are skipped)

export type KindleSource = 'notebook' | 'export' | 'clippings';

export interface PastedNote {
  body: string;
  location: string | null;
  /** The day it was made (only My Clippings.txt has dates). */
  on: string | null;
  key: string;
}

export interface PastedQuote extends PastedNote {
  chapter: string | null;
  /** Your Kindle note on this highlight, if any. */
  note: PastedNote | null;
}

export interface Pasted {
  source: KindleSource | null;
  /** The book the notebook page says the notes are for ("Your Kindle Notes For: …"), if it says. */
  title?: string;
  quotes: PastedQuote[];
  /** Notes with no highlight under them: reading notes kept at their location. */
  notes: PastedNote[];
}

/** The location number used for matching: "Location 229" → 229, else a page ("Page 12" → p12). */
export function placeOf(location: string | null): string {
  if (!location) return '';
  const loc = /loc(?:ation)?\.?\s*:?\s*([\d,]+)/i.exec(location);
  if (loc) return loc[1]!.replace(/,/g, '');
  const page = /page\s*:?\s*([\divxlc]+)/i.exec(location);
  return page ? `p${page[1]!.toLowerCase()}` : '';
}

/** Text for matching: lower case, letters and digits only, the first 40. */
const norm = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '')
    .slice(0, 40);

/** The match key: where it is, plus the first words. The same in every source. */
export const keyOf = (location: string | null, body: string, kind: 'q' | 'n' = 'q') => `${kind}:${placeOf(location)}:${norm(body)}`;

const lines = (t: string) => t.replace(/\r\n?/g, '\n').split('\n');
const tidy = (parts: string[]) => parts.join('\n').replace(/\n{3,}/g, '\n\n').trim();

function notebookPage(text: string): Pasted {
  const quotes: PastedQuote[] = [];
  const notes: PastedNote[] = [];
  // Copied straight from the page (Kristi, Oct 27), "Options" runs onto the heading
  // ("Location: 694Options"), and sometimes the highlight's words follow on the same line.
  const HEAD = /^\s*(?:[a-z]+\s+)?(highlight|note)\s*\|\s*(location|page)\s*:\s*([\d,]+)\s*(?:options)?\s*(.*)$/i;
  let cur: { kind: 'highlight' | 'note'; location: string; body: string[]; note: string[] | null } | null = null;
  const flush = () => {
    if (!cur) return;
    const body = tidy(cur.body);
    if (body && cur.kind === 'highlight') {
      const noteBody = cur.note ? tidy(cur.note) : '';
      quotes.push({
        body,
        location: cur.location,
        chapter: null,
        on: null,
        key: keyOf(cur.location, body),
        note: noteBody ? { body: noteBody, location: cur.location, on: null, key: keyOf(cur.location, body, 'n') } : null,
      });
    } else if (body) {
      notes.push({ body, location: cur.location, on: null, key: keyOf(cur.location, body, 'n') });
    }
    cur = null;
  };
  for (let line of lines(text)) {
    const h = HEAD.exec(line);
    if (h) {
      flush();
      cur = { kind: h[1]!.toLowerCase() as 'highlight' | 'note', location: `${h[2]![0]!.toUpperCase()}${h[2]!.slice(1).toLowerCase()} ${h[3]!.replace(/,/g, '')}`, body: [], note: null };
      if (h[4]!.trim()) cur.body.push(h[4]!);
      continue;
    }
    if (!cur) continue; // the title, author and counts above the first highlight
    if (/^\s*options\s*$/i.test(line)) continue;
    // "Options" run onto the start of the words.
    if (/^\s*options(?=\S)/i.test(line) && !cur.body.length) line = line.replace(/^\s*options/i, '');
    const n = /^\s*note\s*:\s*(.*)$/i.exec(line);
    if (n && cur.kind === 'highlight') {
      cur.note = [n[1]!];
      continue;
    }
    (cur.note ?? cur.body).push(line);
  }
  flush();
  // A whole-page copy names its book under "Your Kindle Notes For:" (Kristi, Oct 27).
  const all = lines(text);
  const at = all.findIndex((l) => /^\s*your kindle notes for\s*:?\s*$/i.test(l));
  const title = at >= 0 ? all.slice(at + 1).find((l) => l.trim())?.trim() : undefined;
  return { source: 'notebook', quotes, notes, ...(title ? { title } : {}) };
}

function exportEmail(text: string): Pasted {
  const quotes: PastedQuote[] = [];
  const notes: PastedNote[] = [];
  const HEAD = /^\s*(highlight\s*\([^)]*\)|note)\s*-\s*(.*)$/i;
  let section: string | null = null;
  let cur: { note: boolean; location: string | null; place: string | null; body: string[] } | null = null;
  const flush = () => {
    if (!cur) return;
    const body = tidy(cur.body);
    if (body) {
      const chapter = [section, cur.place].filter(Boolean).join(' · ') || null;
      if (!cur.note) quotes.push({ body, location: cur.location, chapter, on: null, key: keyOf(cur.location, body), note: null });
      else {
        // A note at the place of the highlight just before it is a note on that highlight.
        const last = quotes.at(-1);
        if (last && !last.note && placeOf(last.location) === placeOf(cur.location)) last.note = { body, location: cur.location, on: null, key: keyOf(cur.location, last.body, 'n') };
        else notes.push({ body, location: cur.location, on: null, key: keyOf(cur.location, body, 'n') });
      }
    }
    cur = null;
  };
  for (const line of lines(text)) {
    const h = HEAD.exec(line);
    if (h) {
      flush();
      // "Page 7 · Location 229", or "3 - Chapter One > Page 7 · Location 229" (the export's "where" before ">").
      const rest = h[2]!.replace(/^\d+\s*-\s*/, '');
      const [where, loc] = rest.includes('>') ? rest.split(/\s*>\s*/, 2) : [null, rest];
      cur = { note: /^note/i.test(h[1]!), location: (loc ?? '').trim() || null, place: where?.trim() || null, body: [] };
    } else if (/^\s*[-–—]{2,}\s*$/.test(line)) flush();
    else if (!cur) {
      if (line.trim()) section = line.trim();
    } else cur.body.push(line);
  }
  flush();
  return { source: 'export', quotes, notes };
}

const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

/** "Added on Monday, January 1, 2024 10:00:00 AM" → 2024-01-01. */
function addedOn(head: string): string | null {
  const m = /added on\s+(?:\w+,\s*)?(\w+)\s+(\d{1,2}),\s*(\d{4})/i.exec(head);
  if (!m) return null;
  const month = MONTHS.indexOf(m[1]!.toLowerCase());
  if (month < 0) return null;
  return `${m[3]}-${String(month + 1).padStart(2, '0')}-${m[2]!.padStart(2, '0')}`;
}

function clippings(text: string, title: string): Pasted {
  const quotes: PastedQuote[] = [];
  const notes: PastedNote[] = [];
  const want = norm(title.split(/[:(]/)[0]!).slice(0, 20);
  for (const block of text.replace(/\r\n?/g, '\n').split(/^={5,}\s*$/m)) {
    const ls = block.split('\n').map((l) => l.replace(/^﻿/, ''));
    while (ls.length && !ls[0]!.trim()) ls.shift();
    if (ls.length < 2) continue;
    if (want && !norm(ls[0]!).startsWith(want)) continue; // another book
    const head = ls[1]!;
    const kind = /your\s+highlight/i.test(head) ? 'highlight' : /your\s+note/i.test(head) ? 'note' : null;
    if (!kind) continue; // a bookmark
    const page = /page\s+([\divxlc]+)/i.exec(head)?.[1];
    const loc = /location\s+([\d,]+)(?:-[\d,]+)?/i.exec(head)?.[1]?.replace(/,/g, '');
    const location = [page ? `Page ${page}` : null, loc ? `Location ${loc}` : null].filter(Boolean).join(' · ') || null;
    const body = tidy(ls.slice(2));
    if (!body) continue;
    const on = addedOn(head);
    if (kind === 'highlight') quotes.push({ body, location, chapter: null, on, key: keyOf(location, body), note: null });
    else {
      // Clippings write a note after its highlight; a note at the highlight's start or end location belongs to it.
      const last = quotes.at(-1);
      const at = Number(placeOf(location));
      const from = last ? Number(placeOf(last.location)) : NaN;
      if (last && !last.note && Number.isFinite(at) && Number.isFinite(from) && at >= from && at - from <= 10)
        last.note = { body, location, on, key: keyOf(last.location, last.body, 'n') };
      else notes.push({ body, location, on, key: keyOf(location, body, 'n') });
    }
  }
  return { source: 'clippings', quotes, notes };
}

/** Reads a paste; which of the three it is comes from what it looks like. */
export function readKindlePaste(text: string, bookTitle: string): Pasted {
  if (/^\s*(?:[a-z]+\s+)?highlight\s*\|\s*(?:location|page)\s*:/im.test(text)) return notebookPage(text);
  if (/^={5,}\s*$/m.test(text) && /-\s*your\s+(highlight|note)/i.test(text)) return clippings(text, bookTitle);
  if (/^\s*(highlight\s*\([^)]*\)|note)\s*-\s*/im.test(text)) return exportEmail(text);
  return { source: null, quotes: [], notes: [] };
}

/** What's already saved, for matching: your quotes and notes, and what you left out before. */
export interface Saved {
  quotes: { id: string; body: string; location: string | null }[];
  notes: { id: string; body: string; location: string | null; quote_id: string | null }[];
  leftOut: string[];
}

export interface Preview {
  /** New quotes, each with its note: what the preview lists with a tick box. */
  quotes: (PastedQuote & { noteState: 'new' | 'saved' | 'changed' | null; savedNoteId: string | null; savedQuoteId: string | null; isNew: boolean })[];
  notes: (PastedNote & { isNew: boolean })[];
  /** Already saved, and left out last time: folded into one line. */
  alreadySaved: number;
  leftOutBefore: number;
}

const sameText = (a: string, b: string) => a.replace(/\s+/g, ' ').trim() === b.replace(/\s+/g, ' ').trim();

/**
 * Sorts a paste against what's saved: new quotes (ticked), quotes already saved (folded), ones
 * left out last time (folded, Kristi Oct 21: left out stays left out), and notes changed in
 * Kindle since (Update mine · Keep mine).
 */
export function previewOf(p: Pasted, saved: Saved): Preview {
  const savedQuoteKey = new Map(saved.quotes.map((q) => [keyOf(q.location, q.body), q] as const));
  const leftOut = new Set(saved.leftOut);
  let alreadySaved = 0;
  let leftOutBefore = 0;
  const quotes: Preview['quotes'] = [];
  for (const q of p.quotes) {
    const have = savedQuoteKey.get(q.key);
    if (leftOut.has(q.key) && !have) {
      leftOutBefore++;
      continue;
    }
    const savedNote = have ? saved.notes.find((n) => n.quote_id === have.id) : undefined;
    const noteState: Preview['quotes'][number]['noteState'] = !q.note ? null : !savedNote ? 'new' : sameText(savedNote.body, q.note.body) ? 'saved' : 'changed';
    if (have && noteState !== 'new' && noteState !== 'changed') {
      alreadySaved++;
      continue;
    }
    quotes.push({ ...q, noteState, savedNoteId: savedNote?.id ?? null, savedQuoteId: have?.id ?? null, isNew: !have });
  }
  const savedNoteKeys = new Set(saved.notes.map((n) => keyOf(n.location, n.body, 'n')));
  const notes: Preview['notes'] = [];
  for (const n of p.notes) {
    if (savedNoteKeys.has(n.key)) alreadySaved++;
    else if (leftOut.has(n.key)) leftOutBefore++;
    else notes.push({ ...n, isNew: true });
  }
  return { quotes, notes, alreadySaved, leftOutBefore };
}

/** Whether a pasted title is this book: the start of the title, ignoring a subtitle or series. */
export function sameBook(pasted: string, title: string): boolean {
  const a = norm(pasted.split(/[:(]/)[0]!);
  const b = norm(title.split(/[:(]/)[0]!);
  return Boolean(a && b) && (a.startsWith(b) || b.startsWith(a));
}
